import React, { useEffect, useMemo, useState } from "react";
import { Edit3, Users, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const EditGroupAttendeeModal = ({
  isOpen,
  onClose,
  onSave,
  attendee,
  initialFormData,
  isLoading = false,
}) => {
  const [form, setForm] = useState(initialFormData || {});

  useEffect(() => {
    if (isOpen) {
      setForm(initialFormData || {});
    }
  }, [isOpen, initialFormData]);

  const user = attendee?.user || {};
  const title = useMemo(() => {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Group Attendee";
  }, [user.first_name, user.last_name]);

  const attendeeSlug = attendee?.slug || attendee?.id || attendee?.user_slug || "N/A";

  const groupAttendees = Array.isArray(form?.attendees) ? form.attendees : [];

  const handleAttendeeChange = (index, field, value) => {
    setForm((prev) => {
      const attendees = Array.isArray(prev.attendees) ? [...prev.attendees] : [];
      attendees[index] = {
        ...(attendees[index] || {}),
        [field]: value,
      };
      return {
        ...prev,
        attendees,
      };
    });
  };

  const handleSubmit = () => {
    const payload = {
      group_info: {
        organization_name: form.groupOrganizationName || "",
        country: form.groupCountry || "",
        city: form.groupCity || "",
      },
      primary_contact_details: {
        primary_contact_name: form.primaryContactName || "",
        primary_contact_phone: form.primaryContactPhone || "",
        primary_contact_email: form.primaryContactEmail || "",
      },
      attendees: Array.isArray(form.attendees)
        ? form.attendees.map((att) => ({
            first_name: att?.first_name || "",
            last_name: att?.last_name || "",
            email: att?.email || "",
            nationality: att?.nationality || "",
            gender: att?.gender || "",
            date_of_birth: att?.date_of_birth || "",
            profession: att?.profession || "",
            organization: att?.organization || "",
            country_of_residence: att?.country_of_residence || "",
            departure_city: att?.departure_city || "",
            address: att?.address || "",
            contact_number: att?.contact_number || "",
          }))
        : [],
    };

    onSave?.(payload);
  };

  return (
    <AnimatePresence>
      {isOpen && attendee && (
        <div className="fixed inset-0 z-50">
          <motion.div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          <motion.aside
            className="fixed right-0 top-0 h-full w-full max-w-3xl bg-white shadow-2xl border-l border-slate-200 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: "easeOut", duration: 0.3 }}
          >
        <div className="px-6 py-5 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Edit3 className="w-5 h-5" />
                Edit Group Registration
              </h3>
              <p className="text-amber-100 text-sm mt-1">
                Update group details and edit each attendee from the API `attendees` array.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-amber-200 p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-slate-50">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-800">
              <span className="font-semibold">Group:</span> {title}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              <span className="font-semibold">ID:</span> {attendeeSlug}
            </p>
          </div>

          <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              Group Details
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Organization Name
              </label>
              <input
                type="text"
                value={form.groupOrganizationName || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    groupOrganizationName: e.target.value,
                  }))
                }
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="e.g. Acme Limited"
                disabled={isLoading}
              />
            </div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              Primary Contact
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Primary Contact Name
              </label>
              <input
                type="text"
                value={form.primaryContactName || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    primaryContactName: e.target.value,
                  }))
                }
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="e.g. Kwabena Acheampong"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Primary Contact Phone
              </label>
              <input
                type="text"
                value={form.primaryContactPhone || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    primaryContactPhone: e.target.value,
                  }))
                }
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="e.g. 0249158582"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Primary Contact Email
              </label>
              <input
                type="email"
                value={form.primaryContactEmail || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    primaryContactEmail: e.target.value,
                  }))
                }
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="e.g. name@email.com"
                disabled={isLoading}
              />
            </div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Group Attendees ({groupAttendees.length})
              </div>
            </div>

            {groupAttendees.length === 0 ? (
              <p className="text-sm text-slate-500">
                No attendees found in the registration payload.
              </p>
            ) : (
              <div className="space-y-4">
                {groupAttendees.map((att, idx) => (
                  <div
                    key={`${att?.email || att?.slug || idx}`}
                    className="rounded-lg border border-slate-200 p-4 bg-slate-50"
                  >
                    <div className="text-sm font-semibold text-slate-700 mb-3">
                      Attendee {idx + 1}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.first_name || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "first_name", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="First name"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.last_name || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "last_name", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Last name"
                        disabled={isLoading}
                      />
                      <input
                        type="email"
                        value={form?.attendees?.[idx]?.email || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "email", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 md:col-span-2"
                        placeholder="Email"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.contact_number || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "contact_number", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Contact number"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.nationality || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "nationality", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Nationality"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.gender || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "gender", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Gender"
                        disabled={isLoading}
                      />
                      <input
                        type="date"
                        value={form?.attendees?.[idx]?.date_of_birth || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "date_of_birth", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.profession || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "profession", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Profession"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.organization || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "organization", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Organization"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.country_of_residence || ""}
                        onChange={(e) =>
                          handleAttendeeChange(
                            idx,
                            "country_of_residence",
                            e.target.value
                          )
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Country of residence"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.departure_city || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "departure_city", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Departure city"
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={form?.attendees?.[idx]?.address || ""}
                        onChange={(e) =>
                          handleAttendeeChange(idx, "address", e.target.value)
                        }
                        className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 md:col-span-2"
                        placeholder="Address"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <p className="text-sm text-gray-600 mt-4">
            Save will call the admin update API for this group registration.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className={`px-6 py-2 rounded-lg font-medium text-white transition-colors ${
              isLoading ? "bg-amber-400 cursor-not-allowed" : "bg-amber-600 hover:bg-amber-700"
            }`}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </button>
        </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EditGroupAttendeeModal;

